import { useDeleteSubstitutionsMutation } from 'store/services/core/user';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface UseSubstitutionDeleteOptions {
  redirectPath?: string;
  onSuccess?: () => void;
  validateBeforeDelete?: (substitution: { dateFrom: Date; id: string }) => boolean;
  errorMessage?: string;
}

export const useSubstitutionDelete = (options: UseSubstitutionDeleteOptions = {}) => {
  const { redirectPath, onSuccess, validateBeforeDelete, errorMessage } = options;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [triggerDeleteSubstitution, { isLoading }] = useDeleteSubstitutionsMutation();

  const deleteSubstitution = useCallback(
    async (substitutionId: string, substitution?: { dateFrom: Date; id: string }) => {
      if (validateBeforeDelete && substitution && !validateBeforeDelete(substitution)) {
        if (errorMessage) {
          toast.error(errorMessage);
        }
        return false;
      }

      const res = await triggerDeleteSubstitution({
        ids: [substitutionId],
      });

      if (!('error' in res)) {
        toast.success(t('substitution.successfullyDeleted'));

        if (redirectPath) {
          navigate(redirectPath, { replace: true });
        }

        if (onSuccess) {
          onSuccess();
        }

        return true;
      }

      return false;
    },
    [triggerDeleteSubstitution, t, navigate, redirectPath, onSuccess, validateBeforeDelete, errorMessage]
  );

  const deleteMultipleSubstitutions = useCallback(
    async (substitutionIds: string[], substitutions?: { dateFrom: Date; id: string }[]) => {
      if (validateBeforeDelete && substitutions) {
        const invalidSubstitutions = substitutions.filter((sub) => !validateBeforeDelete(sub));
        if (invalidSubstitutions.length > 0) {
          if (errorMessage) {
            toast.error(errorMessage);
          }
          return false;
        }
      }

      const res = await triggerDeleteSubstitution({
        ids: substitutionIds,
      });

      if (!('error' in res)) {
        toast.success(t('substitution.successfullyDeleted'));

        if (onSuccess) {
          onSuccess();
        }

        return true;
      }

      return false;
    },
    [triggerDeleteSubstitution, t, onSuccess, validateBeforeDelete, errorMessage]
  );

  return {
    deleteSubstitution,
    deleteMultipleSubstitutions,
    isDeleting: isLoading,
  };
};
